import type { PortfolioItem, PaymentRecord } from '../types/interfaces';
import { formatCurrency } from './currency';

export type ExportFormat = 'csv' | 'json' | 'pdf';

export interface ExportData {
  portfolio: PortfolioItem[];
  payments: PaymentRecord[];
  exportDate: string;
  currency: string;
}

// Export portfolio to CSV
export function exportToCSV(data: ExportData): void {
  const rows: string[] = [];
  
  // Header
  rows.push('Coin Name,Symbol,Quantity,Purchase Price,Current Price,Total Value,Total Cost,Profit/Loss,ROI %');
  
  // Portfolio rows
  data.portfolio.forEach(item => {
    const quantity = item.quantity || 0;
    const purchasePrice = item.purchasePrice || item.currentPrice || 0;
    const currentPrice = item.currentPrice || 0;
    const totalValue = quantity * currentPrice;
    const totalCost = quantity * purchasePrice;
    const profitLoss = totalValue - totalCost;
    const roi = totalCost > 0 ? (profitLoss / totalCost) * 100 : 0;
    
    rows.push([
      `"${item.name}"`,
      item.symbol.toUpperCase(),
      quantity.toString(),
      purchasePrice.toFixed(8),
      currentPrice.toFixed(8),
      totalValue.toFixed(2),
      totalCost.toFixed(2),
      profitLoss.toFixed(2),
      roi.toFixed(2)
    ].join(','));
  });
  
  // Summary row
  const totalValue = data.portfolio.reduce((sum, item) => 
    sum + (item.quantity || 0) * (item.currentPrice || 0), 0
  );
  const totalCost = data.portfolio.reduce((sum, item) => 
    sum + (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0), 0
  );
  const totalProfit = totalValue - totalCost;
  const totalROI = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
  
  rows.push('');
  rows.push('SUMMARY');
  rows.push(`Total Value,${totalValue.toFixed(2)}`);
  rows.push(`Total Cost,${totalCost.toFixed(2)}`);
  rows.push(`Total Profit/Loss,${totalProfit.toFixed(2)}`);
  rows.push(`Total ROI,${totalROI.toFixed(2)}%`);
  
  // Payment records section
  if (data.payments.length > 0) {
    rows.push('');
    rows.push('PAYMENT RECORDS');
    rows.push('Action,Amount (satoshis),Date,Transaction ID,Protocol');
    data.payments.forEach(payment => {
      const date = new Date(payment.timestamp).toISOString();
      rows.push([
        payment.action,
        payment.amount.toString(),
        date,
        payment.txId,
        payment.protocol
      ].join(','));
    });
  }
  
  const csvContent = rows.join('\n');
  downloadFile(csvContent, `portfolio-export-${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
}

// Export to JSON
export function exportToJSON(data: ExportData): void {
  const exportData = {
    exportDate: data.exportDate,
    currency: data.currency,
    portfolio: data.portfolio.map(item => ({
      id: item.id,
      name: item.name,
      symbol: item.symbol,
      quantity: item.quantity,
      purchasePrice: item.purchasePrice,
      currentPrice: item.currentPrice,
      change24h: item.change24h,
      calculatedValue: (item.quantity || 0) * (item.currentPrice || 0),
      calculatedCost: (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0),
      calculatedProfit: ((item.quantity || 0) * (item.currentPrice || 0)) - ((item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0))
    })),
    payments: data.payments,
    summary: {
      totalValue: data.portfolio.reduce((sum, item) => 
        sum + (item.quantity || 0) * (item.currentPrice || 0), 0
      ),
      totalCost: data.portfolio.reduce((sum, item) => 
        sum + (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0), 0
      ),
      totalProfit: data.portfolio.reduce((sum, item) => {
        const value = (item.quantity || 0) * (item.currentPrice || 0);
        const cost = (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0);
        return sum + (value - cost);
      }, 0),
      assetCount: data.portfolio.length
    }
  };
  
  const jsonContent = JSON.stringify(exportData, null, 2);
  downloadFile(jsonContent, `portfolio-export-${new Date().toISOString().split('T')[0]}.json`, 'application/json');
}

// Export to PDF (HTML format that can be printed to PDF)
export function exportToPDF(data: ExportData): void {
  const totalValue = data.portfolio.reduce((sum, item) => 
    sum + (item.quantity || 0) * (item.currentPrice || 0), 0
  );
  const totalCost = data.portfolio.reduce((sum, item) => 
    sum + (item.quantity || 0) * (item.purchasePrice || item.currentPrice || 0), 0
  );
  const totalProfit = totalValue - totalCost;
  const totalROI = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Portfolio Export - ${data.exportDate}</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 40px;
      color: #1e293b;
      background: #ffffff;
    }
    h1 {
      color: #6366f1;
      border-bottom: 3px solid #6366f1;
      padding-bottom: 10px;
      margin-bottom: 30px;
    }
    h2 {
      color: #475569;
      margin-top: 30px;
      margin-bottom: 15px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #e2e8f0;
    }
    th {
      background-color: #f1f5f9;
      font-weight: bold;
      color: #334155;
    }
    tr:hover {
      background-color: #f8fafc;
    }
    .summary {
      background: #f1f5f9;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .summary-item {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #e2e8f0;
    }
    .summary-item:last-child {
      border-bottom: none;
    }
    .positive {
      color: #10b981;
      font-weight: bold;
    }
    .negative {
      color: #ef4444;
      font-weight: bold;
    }
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 2px solid #e2e8f0;
      color: #64748b;
      font-size: 12px;
      text-align: center;
    }
    @media print {
      body {
        padding: 20px;
      }
    }
  </style>
</head>
<body>
  <h1>📊 Portfolio Export</h1>
  <p><strong>Export Date:</strong> ${data.exportDate}</p>
  <p><strong>Currency:</strong> ${data.currency.toUpperCase()}</p>
  
  <div class="summary">
    <h2>Portfolio Summary</h2>
    <div class="summary-item">
      <span>Total Value:</span>
      <strong>${formatCurrency(totalValue, data.currency)}</strong>
    </div>
    <div class="summary-item">
      <span>Total Cost:</span>
      <strong>${formatCurrency(totalCost, data.currency)}</strong>
    </div>
    <div class="summary-item">
      <span>Total Profit/Loss:</span>
      <strong class="${totalProfit >= 0 ? 'positive' : 'negative'}">
        ${totalProfit >= 0 ? '+' : ''}${formatCurrency(totalProfit, data.currency)}
      </strong>
    </div>
    <div class="summary-item">
      <span>Total ROI:</span>
      <strong class="${totalROI >= 0 ? 'positive' : 'negative'}">
        ${totalROI >= 0 ? '+' : ''}${totalROI.toFixed(2)}%
      </strong>
    </div>
    <div class="summary-item">
      <span>Number of Assets:</span>
      <strong>${data.portfolio.length}</strong>
    </div>
  </div>
  
  <h2>Portfolio Holdings</h2>
  <table>
    <thead>
      <tr>
        <th>Coin</th>
        <th>Symbol</th>
        <th>Quantity</th>
        <th>Purchase Price</th>
        <th>Current Price</th>
        <th>Total Value</th>
        <th>Total Cost</th>
        <th>Profit/Loss</th>
        <th>ROI %</th>
      </tr>
    </thead>
    <tbody>
      ${data.portfolio.map(item => {
        const quantity = item.quantity || 0;
        const purchasePrice = item.purchasePrice || item.currentPrice || 0;
        const currentPrice = item.currentPrice || 0;
        const totalValue = quantity * currentPrice;
        const totalCost = quantity * purchasePrice;
        const profitLoss = totalValue - totalCost;
        const roi = totalCost > 0 ? (profitLoss / totalCost) * 100 : 0;
        
        return `
        <tr>
          <td>${item.name}</td>
          <td>${item.symbol.toUpperCase()}</td>
          <td>${quantity.toFixed(8)}</td>
          <td>${formatCurrency(purchasePrice, data.currency)}</td>
          <td>${formatCurrency(currentPrice, data.currency)}</td>
          <td>${formatCurrency(totalValue, data.currency)}</td>
          <td>${formatCurrency(totalCost, data.currency)}</td>
          <td class="${profitLoss >= 0 ? 'positive' : 'negative'}">
            ${profitLoss >= 0 ? '+' : ''}${formatCurrency(profitLoss, data.currency)}
          </td>
          <td class="${roi >= 0 ? 'positive' : 'negative'}">
            ${roi >= 0 ? '+' : ''}${roi.toFixed(2)}%
          </td>
        </tr>
        `;
      }).join('')}
    </tbody>
  </table>
  
  ${data.payments.length > 0 ? `
  <h2>Payment Records</h2>
  <table>
    <thead>
      <tr>
        <th>Action</th>
        <th>Amount (satoshis)</th>
        <th>Date</th>
        <th>Transaction ID</th>
        <th>Protocol</th>
      </tr>
    </thead>
    <tbody>
      ${data.payments.map(payment => `
        <tr>
          <td>${payment.action}</td>
          <td>${payment.amount}</td>
          <td>${new Date(payment.timestamp).toLocaleString()}</td>
          <td>${payment.txId}</td>
          <td>${payment.protocol}</td>
        </tr>
      `).join('')}
    </tbody>
  </table>
  ` : ''}
  
  <div class="footer">
    <p>Generated by CryptoScope on ${data.exportDate}</p>
    <p>This is a snapshot of your portfolio at the time of export.</p>
  </div>
</body>
</html>
  `;
  
  downloadFile(html, `portfolio-export-${new Date().toISOString().split('T')[0]}.html`, 'text/html');
  
  // Open in new window for printing
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const printWindow = window.open(url, '_blank');
  if (printWindow) {
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  }
}

// Helper function to download file
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Prepare export data
export function prepareExportData(
  portfolio: PortfolioItem[],
  currency: string = 'usd'
): ExportData {
  const payments: PaymentRecord[] = JSON.parse(localStorage.getItem('bsvPayments') || '[]');
  
  return {
    portfolio,
    payments,
    exportDate: new Date().toLocaleString(),
    currency
  };
}

